import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Rss, Twitch, Youtube, Twitter, Instagram, Facebook, Video, Radio } from 'lucide-react';
import { Loader2, Save, Rss, Twitch, Youtube, Twitter, Instagram, Facebook, Video, Radio, UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function SocialAlertsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const { data: socialAlerts = [], isLoading: alertsLoading } = useQuery({
    queryKey: ['servers', serverId, 'social-alerts'],
    queryFn: () => pluginApi.getSocialAlerts(serverId),
  });

  const socialAlertsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'social-alerts');
  const isEnabled = socialAlertsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'social-alerts') : pluginApi.disablePlugin(serverId, 'social-alerts'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'social-alerts', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    alertChannelId: '',
    twitchMessage: '🔴 {username} is now live on Twitch! {url}',
    youtubeMessage: '📹 {username} uploaded a new video: {title} {url}',
    twitterMessage: '🐦 New tweet from {username}: {url}',
    instagramMessage: '📸 New Instagram post from {username}: {url}',
    tiktokMessage: '🎵 New TikTok from {username}: {url}',
    facebookMessage: '📘 New Facebook update from {username}: {url}',
    kickMessage: '🟢 {username} is live on Kick! {url}',
    enableTwitch: true,
    enableYouTube: true,
    enableTwitter: true,
    enableInstagram: true,
    enableTikTok: true,
    enableFacebook: true,
    enableKick: true,
    mentionRole: '',
    pollIntervalSeconds: 60,
  });

  const [streamerUsername, setStreamerUsername] = useState('');

  const { mutate: addStreamer, isPending: addingStreamer } = useApiMutation(
    () => pluginApi.addSocialAlert(serverId, {
      platform: 'twitch',
      username: streamerUsername.trim(),
      channelId: config.alertChannelId,
    }),
    {
      onSuccess: () => {
        setStreamerUsername('');
        toast.success('Streamer aggiunto al monitoraggio');
      },
      onError: (err: any) => toast.error(err?.message || 'Impossibile aggiungere lo streamer'),
      invalidateQueries: [['servers', serverId, 'social-alerts']],
    }
  );

  const { mutate: removeStreamer, isPending: removingStreamer } = useApiMutation(
    (alertId: string) => pluginApi.removeSocialAlert(serverId, alertId),
    {
      onSuccess: () => toast.success('Streamer rimosso'),
      onError: (err: any) => toast.error(err?.message || 'Impossibile rimuovere lo streamer'),
      invalidateQueries: [['servers', serverId, 'social-alerts']],
    }
  );

  useEffect(() => {
    if (socialAlertsPlugin?.config) {
      setConfig({ ...config, ...socialAlertsPlugin.config });
    }
  }, [socialAlertsPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!socialAlertsPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Rss className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Social Media Alerts</h3>
            <p className="text-sm text-muted-foreground">
              Get notified when streamers go live or post content.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div>
              <h4 className="flex items-center gap-2 font-semibold"><UserPlus className="h-5 w-5 text-emerald-400" /> Streamer monitorati</h4>
              <p className="mt-1 text-sm text-muted-foreground">Aggiungi gli username Twitch. Il bot controllerà quando iniziano una nuova live.</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={streamerUsername}
                onChange={(event) => setStreamerUsername(event.target.value)}
                placeholder="Username Twitch, es. xqc"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && streamerUsername.trim() && config.alertChannelId) addStreamer();
                }}
              />
              <Button
                onClick={() => addStreamer()}
                disabled={addingStreamer || !streamerUsername.trim() || !config.alertChannelId}
                className="gap-2 sm:min-w-44"
              >
                {addingStreamer ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Aggiungi streamer
              </Button>
            </div>
            {!config.alertChannelId && <p className="text-xs text-amber-500">Seleziona prima il canale notifiche qui sotto.</p>}
            <div className="space-y-2">
              {alertsLoading && <p className="text-sm text-muted-foreground">Caricamento streamer...</p>}
              {!alertsLoading && socialAlerts.length === 0 && <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">Nessuno streamer monitorato.</p>}
              {socialAlerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between rounded-lg border border-border/70 bg-background/40 p-3">
                  <div className="flex items-center gap-2"><Twitch className="h-4 w-4 text-purple-400" /><span className="font-medium">@{alert.username}</span><span className="text-xs text-muted-foreground">Twitch</span></div>
                  <Button type="button" variant="ghost" size="icon" disabled={removingStreamer} onClick={() => removeStreamer(alert.id)} aria-label={`Rimuovi ${alert.username}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <div>
              <Label>Canale notifiche live</Label>
              <Select
                value={config.alertChannelId || 'none'}
                onValueChange={(value) => setConfig({ ...config, alertChannelId: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona canale alert" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Ruolo da menzionare (opzionale)</Label>
              <Select
                value={config.mentionRole || 'none'}
                onValueChange={(value) => setConfig({ ...config, mentionRole: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ruolo da menzionare" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun ruolo</SelectItem>
                  {(roles as any[]).map((role: any) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Controllo live ogni (secondi)</Label>
                <Input
                  type="number"
                  min={30}
                  max={600}
                  value={config.pollIntervalSeconds}
                  onChange={(e) => setConfig({ ...config, pollIntervalSeconds: Math.min(600, Math.max(30, Number(e.target.value) || 60)) })}
                />
              </div>
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-sm text-muted-foreground">
                Twitch invia una notifica quando rileva l’inizio di una nuova live. Servono `TWITCH_CLIENT_ID` e `TWITCH_CLIENT_SECRET` nel backend.
              </div>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Twitch className="w-5 h-5 text-purple-400" />
              <h4 className="font-semibold">Twitch Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable Twitch</Label>
              <Switch checked={config.enableTwitch} onCheckedChange={(checked) => setConfig({ ...config, enableTwitch: checked })} />
            </div>
            <Input
              value={config.twitchMessage}
              onChange={(e) => setConfig({ ...config, twitchMessage: e.target.value })}
              placeholder="🔴 {username} is now live!"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-red-400" />
              <h4 className="font-semibold">YouTube Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable YouTube</Label>
              <Switch checked={config.enableYouTube} onCheckedChange={(checked) => setConfig({ ...config, enableYouTube: checked })} />
            </div>
            <Input
              value={config.youtubeMessage}
              onChange={(e) => setConfig({ ...config, youtubeMessage: e.target.value })}
              placeholder="📹 {username} uploaded a new video: {title} {url}"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Twitter className="w-5 h-5 text-sky-400" />
              <h4 className="font-semibold">X / Twitter Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable X</Label>
              <Switch checked={config.enableTwitter} onCheckedChange={(checked) => setConfig({ ...config, enableTwitter: checked })} />
            </div>
            <Input
              value={config.twitterMessage}
              onChange={(e) => setConfig({ ...config, twitterMessage: e.target.value })}
              placeholder="🐦 New tweet from {username}: {url}"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Instagram className="w-5 h-5 text-pink-400" />
              <h4 className="font-semibold">Instagram Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable Instagram</Label>
              <Switch checked={config.enableInstagram} onCheckedChange={(checked) => setConfig({ ...config, enableInstagram: checked })} />
            </div>
            <Input
              value={config.instagramMessage}
              onChange={(e) => setConfig({ ...config, instagramMessage: e.target.value })}
              placeholder="📸 New Instagram post from {username}: {url}"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Video className="w-5 h-5 text-fuchsia-400" />
              <h4 className="font-semibold">TikTok Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable TikTok</Label>
              <Switch checked={config.enableTikTok} onCheckedChange={(checked) => setConfig({ ...config, enableTikTok: checked })} />
            </div>
            <Input
              value={config.tiktokMessage}
              onChange={(e) => setConfig({ ...config, tiktokMessage: e.target.value })}
              placeholder="🎵 New TikTok from {username}: {url}"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Facebook className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Facebook Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable Facebook</Label>
              <Switch checked={config.enableFacebook} onCheckedChange={(checked) => setConfig({ ...config, enableFacebook: checked })} />
            </div>
            <Input
              value={config.facebookMessage}
              onChange={(e) => setConfig({ ...config, facebookMessage: e.target.value })}
              placeholder="📘 New Facebook update from {username}: {url}"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-5 h-5 text-emerald-400" />
              <h4 className="font-semibold">Kick Alert Message</h4>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <Label>Enable Kick</Label>
              <Switch checked={config.enableKick} onCheckedChange={(checked) => setConfig({ ...config, enableKick: checked })} />
            </div>
            <Input
              value={config.kickMessage}
              onChange={(e) => setConfig({ ...config, kickMessage: e.target.value })}
              placeholder="🟢 {username} is live on Kick! {url}"
            />
          </div>

          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold">Available Commands</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-purple-400">/addalert [platform] [username]</code>
                <p className="text-muted-foreground mt-1">Add a social media alert</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-purple-400">/removealert [platform] [username]</code>
                <p className="text-muted-foreground mt-1">Remove an alert</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-purple-400">/alerts</code>
                <p className="text-muted-foreground mt-1">List all active alerts</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
